import { baseApi } from "./baseApi";
// ✅ We no longer need loginStart or loginFailure here for these mutations
import { loginSuccess, logout } from "../slices/authSlice";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/dashboarduser/login",
        method: "POST",
        body: credentials,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }),
      // ✅ SIMPLIFIED: onQueryStarted now only handles the SUCCESS side-effect.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // ✅ KEPT: On a successful query, we still need to update the global
          // auth state with the token and user data. This is a critical side-effect.
          dispatch(
            loginSuccess({
              token: data.token,
              user: data.user,
              role: data.role || data.user?.role,
            })
          );
        } catch (error) {
          // ✅ REMOVED: We no longer dispatch loginFailure. The component's
          // useLoginMutation hook will now handle the error state for the UI.
          console.error("Login query failed:", error);
        }
      },
      invalidatesTags: ["Auth"],
    }),

    logout: builder.mutation({
      query: () => ({
        url: "/dashboarduser/logout",
        method: "POST",
        body: {},
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }),
      async onQueryStarted(arg, { dispatch }) {
        dispatch(logout());
      },
      invalidatesTags: ["Auth"],
    }),

    refreshToken: builder.mutation({
      query: () => ({
        url: "/dashboarduser/refresh",
        method: "POST",
        body: {},
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            loginSuccess({
              token: data.token,
              user: data.user,
              role: data.role || data.user?.role,
            })
          );
        } catch (error) {
          dispatch(logout());
        }
      },
      invalidatesTags: ["Auth"],
    }),

    getProfile: builder.query({
      query: () => "/dashboarduser/profile",
      providesTags: ["Auth"],
    }),

    updateProfile: builder.mutation({
      query: (updateData) => ({
        url: "/dashboarduser/profile",
        method: "PUT",
        body: {
          username: updateData.username,
          organization: updateData.organisation,
          country: updateData.country,
          stationName: updateData.stationName,
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            loginSuccess({
              token: undefined,
              user: data.user,
              role: data.user.role,
            })
          );
        } catch (error) {
          console.error("Profile update failed:", error);
        }
      },
      invalidatesTags: ["Auth"],
    }),

    getGameSettings: builder.query({
      query: () => ({
        url: "/dashboarduser/game-settings",
        method: "GET",
      }),
      transformResponse: (response) => response?.settings ?? response,
      providesTags: ["Settings"],
    }),

    updateGameSettings: builder.mutation({
      query: (settings) => ({
        url: "/dashboarduser/game-settings",
        method: "PUT",
        body: settings,
      }),
      transformResponse: (response) => response?.settings ?? response,
      invalidatesTags: ["Settings"],
    }),

    getAllUsers: builder.query({
      query: ({ page = 1, limit = 10, role, isActive, search } = {}) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (role) params.append("role", role);
        if (search) params.append("search", search);

        const url = `/dashboarduser/users?${params.toString()}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Users"],
    }),

    getUserById: builder.query({
      query: (userId) => `/dashboarduser/users/${userId}`,
      providesTags: (result, error, userId) => [{ type: "Users", id: userId }],
    }),

    updateUser: builder.mutation({
      query: ({ userId, updateData }) => ({
        url: `/dashboarduser/users/${userId}`,
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: (result, error, { userId }) => [
        "Users",
        { type: "Users", id: userId },
      ],
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/dashboarduser/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),

    toggleUserStatus: builder.mutation({
      query: (userId) => ({
        url: `/dashboarduser/users/${userId}/toggle-status`,
        method: "PATCH",
      }),
      invalidatesTags: ["Users"],
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: "/dashboarduser/users",
        method: "POST",
        body: {
          username: userData.username,
          email: userData.email,
          password: userData.password,
          role: userData.role,
          serviceCode: userData.serviceCode,
          organization: userData.organization || userData.organisation,
          country: userData.country,
          ...(String(userData.stationName || '').trim()
            ? { stationName: String(userData.stationName).trim() }
            : {}),
        },
      }),
      invalidatesTags: ["Users"],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetGameSettingsQuery,
  useUpdateGameSettingsMutation,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useToggleUserStatusMutation,
  useCreateUserMutation,
  // ... any other hooks you export
} = authApi;

// // src/store/api/authApi.js
// import { baseApi } from './baseApi';
// import { loginSuccess, loginFailure, loginStart, logout } from '../slices/authSlice';

// export const authApi = baseApi.injectEndpoints({
//   endpoints: (builder) => ({
//     login: builder.mutation({
//       query: (credentials) => ({
//         url: '/dashboarduser/login',
//         method: 'POST',
//         body: {
//           email:credentials.email,
//           password: credentials.password,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           console.log('Login successful, storing tokens:', {
//             hasToken: !!data.token,
//             hasRefreshToken: !!data.refreshToken,
//             userRole: data.role || data.user?.role
//           });

//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           console.error('Login error:', error);
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Login failed. Please check your credentials.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     register: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register',
//         method: 'POST',
//         body: {
//           username: userData.username,
//           email: userData.email,
//           password: userData.password,
//           role: userData.role,
//           serviceCode: userData.serviceCode,
//           organization: userData.organization,
//           country: userData.country,
//           stationName: userData.stationName,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Registration failed. Please try again.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     logout: builder.mutation({
//       query: () => ({
//         url: '/dashboarduser/logout',
//         method: 'POST',
//       }),
//       async onQueryStarted(arg, { dispatch }) {
//         dispatch(logout());
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     refreshToken: builder.mutation({
//       query: (refreshToken) => ({
//         url: '/dashboarduser/refresh',
//         method: 'POST',
//         body: { refreshToken },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           dispatch(logout());
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     getProfile: builder.query({
//       query: () => '/dashboarduser/profile',
//       providesTags: ['Auth'],
//     }),

//     updateProfile: builder.mutation({
//       query: (updateData) => ({
//         url: '/dashboarduser/profile',
//         method: 'PUT',
//         body: {
//           username: updateData.username,
//           organization: updateData.organisation,
//           country: updateData.country,
//           stationName: updateData.stationName,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: undefined,
//             refreshToken: undefined,
//             user: data.user,
//             role: data.user.role,
//           }));
//         } catch (error) {
//           console.error('Profile update failed:', error);
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     // ====================================
//     // USER MANAGEMENT ENDPOINTS
//     // ====================================

//     // Enhanced getAllUsers with better debugging
//     getAllUsers: builder.query({
//       query: ({ page = 1, limit = 10, role, isActive, search } = {}) => {
//         const params = new URLSearchParams({
//           page: page.toString(),
//           limit: limit.toString(),
//         });

//         if (role) params.append('role', role);
//         //if (isActive !== undefined) params.append('isActive', isActive.toString());
//         if (search) params.append('search', search);

//         const url = `/dashboarduser/users?${params.toString()}`;
//         console.log('Making getAllUsers request to:', url);

//         return {
//           url,
//           method: 'GET',
//         };
//       },
//       // Add debugging for the request lifecycle
//       async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
//         const state = getState();
//         console.log('getAllUsers query started:', {
//           hasToken: !!state.auth.token,
//           userRole: state.auth.role,
//           isAuthenticated: state.auth.isAuthenticated,
//           tokenPreview: state.auth.token ? `${state.auth.token.substring(0, 20)}...` : 'No token'
//         });

//         try {
//           const result = await queryFulfilled;
//           console.log('getAllUsers success:', {
//             userCount: result.data?.users?.length || 0,
//             totalPages: result.data?.totalPages,
//             currentPage: result.data?.currentPage
//           });
//         } catch (error) {
//           console.error('getAllUsers error:', {
//             status: error.error?.status,
//             message: error.error?.data?.message || error.error?.data,
//             originalStatus: error.error?.originalStatus
//           });

//           // If it's a 401 error, let's check the token status
//           if (error.error?.status === 401) {
//             console.log('401 Error - Current auth state:', {
//               hasToken: !!state.auth.token,
//               hasRefreshToken: !!state.auth.refreshToken,
//               role: state.auth.role
//             });
//           }
//         }
//       },
//       providesTags: ['Users'],
//     }),

//     // Test endpoint to verify authentication
//     testAuth: builder.query({
//       query: () => ({
//         url: '/dashboarduser/profile', // Use profile endpoint to test auth
//         method: 'GET',
//       }),
//       async onQueryStarted(arg, { getState, queryFulfilled }) {
//         const state = getState();
//         console.log('Testing authentication:', {
//           hasToken: !!state.auth.token,
//           tokenLength: state.auth.token?.length,
//           role: state.auth.role,
//           isAuthenticated: state.auth.isAuthenticated
//         });

//         try {
//           const result = await queryFulfilled;
//           console.log('Auth test successful:', result.data);
//         } catch (error) {
//           console.error('Auth test failed:', error);
//         }
//       },
//     }),

//     getUserById: builder.query({
//       query: (userId) => `/dashboarduser/users/${userId}`,
//       providesTags: (result, error, userId) => [{ type: 'Users', id: userId }],
//     }),

//     updateUser: builder.mutation({
//       query: ({ userId, updateData }) => ({
//         url: `/dashboarduser/users/${userId}`,
//         method: 'PUT',
//         body: {
//           username: updateData.username,
//           email: updateData.email,
//           role: updateData.role,
//           serviceCode: updateData.serviceCode,
//           organization: updateData.organisation || updateData.organization,
//           country: updateData.country,
//           stationName: updateData.stationName,
//           isActive: updateData.isActive,
//         },
//       }),
//       invalidatesTags: (result, error, { userId }) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     deleteUser: builder.mutation({
//       query: (userId) => ({
//         url: `/dashboarduser/users/${userId}`,
//         method: 'DELETE',
//       }),
//       invalidatesTags: (result, error, userId) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     toggleUserStatus: builder.mutation({
//       query: (userId) => ({
//         url: `/dashboarduser/users/${userId}/toggle-status`,
//         method: 'PATCH',
//       }),
//       invalidatesTags: (result, error, userId) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     createUser: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register',
//         method: 'POST',
//         body: {
//           username: userData.username,
//           email: userData.email,
//           password: userData.password,
//           role: userData.role,
//           serviceCode: userData.serviceCode,
//           organization: userData.organisation || userData.organization,
//           country: userData.country,
//           stationName: userData.stationName,
//         },
//       }),
//       invalidatesTags: ['Users'],
//     }),

//     bulkUpdateUsers: builder.mutation({
//       query: ({ userIds, updateData }) => ({
//         url: '/dashboarduser/users/bulk-update',
//         method: 'PATCH',
//         body: {
//           userIds,
//           updateData: {
//             role: updateData.role,
//             isActive: updateData.isActive,
//             organization: updateData.organisation || updateData.organization,
//             country: updateData.country,
//           },
//         },
//       }),
//       invalidatesTags: ['Users'],
//     }),

//     getUsersByRole: builder.query({
//       query: (role) => `/dashboarduser/users?role=${role}&limit=1000`,
//       providesTags: (result, error, role) => [{ type: 'Users', id: `ROLE_${role}` }],
//     }),

//     getUserStats: builder.query({
//       query: () => '/dashboarduser/users/stats',
//       providesTags: ['UserStats'],
//     }),
//   }),
// });

// export const {
//   // Existing auth hooks
//   useLoginMutation,
//   useRegisterMutation,
//   useLogoutMutation,
//   useRefreshTokenMutation,
//   useGetProfileQuery,
//   useUpdateProfileMutation,

//   // User management hooks
//   useGetAllUsersQuery,
//   useGetUserByIdQuery,
//   useUpdateUserMutation,
//   useDeleteUserMutation,
//   useToggleUserStatusMutation,
//   useCreateUserMutation,
//   useBulkUpdateUsersMutation,
//   useGetUsersByRoleQuery,
//   useGetUserStatsQuery,

//   // Debug hooks
//   useTestAuthQuery,

//   // Lazy queries for conditional loading
//   useLazyGetAllUsersQuery,
//   useLazyGetUserByIdQuery,
//   useLazyGetUsersByRoleQuery,
// } = authApi;

// // src/store/api/authApi.js
// import { baseApi } from './baseApi';
// import { loginSuccess, loginFailure, loginStart, logout } from '../slices/authSlice';

// export const authApi = baseApi.injectEndpoints({
//   endpoints: (builder) => ({
//     login: builder.mutation({
//       query: (credentials) => ({
//         url: '/dashboarduser/login',
//         method: 'POST',
//         body: {
//           // Map frontend field to backend expected field
//           email: credentials.identifier, // Your login form uses 'identifier' but backend expects 'email'
//           password: credentials.password,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Add refreshToken support
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Login failed. Please check your credentials.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     register: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register',
//         method: 'POST',
//         body: {
//           // Map frontend fields to backend expected fields
//           username: userData.username,
//           email: userData.email,
//           password: userData.password,
//           role: userData.role,
//           serviceCode: userData.serviceCode,
//           organization: userData.organisation, // Frontend uses 'organisation', backend expects 'organization'
//           country: userData.country,
//           stationName: userData.stationName, // Optional field
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Add refreshToken support
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Registration failed. Please try again.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     logout: builder.mutation({
//       query: () => ({
//         url: '/dashboarduser/logout',
//         method: 'POST',
//       }),
//       async onQueryStarted(arg, { dispatch }) {
//         // Clear auth state immediately
//         dispatch(logout()); // Use the correct logout action
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     refreshToken: builder.mutation({
//       query: (refreshToken) => ({
//         url: '/dashboarduser/refresh',
//         method: 'POST',
//         body: { refreshToken }, // Send refreshToken in body as expected by backend
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Update with new refreshToken
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           dispatch(logout()); // Use the correct logout action
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     getProfile: builder.query({
//       query: () => '/dashboarduser/profile',
//       providesTags: ['Auth'],
//     }),

//     updateProfile: builder.mutation({
//       query: (updateData) => ({
//         url: '/dashboarduser/profile',
//         method: 'PUT',
//         body: {
//           // Map frontend fields to backend expected fields
//           username: updateData.username,
//           organization: updateData.organisation, // Frontend uses 'organisation'
//           country: updateData.country,
//           stationName: updateData.stationName,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           // Update user data in state without affecting tokens
//           dispatch(loginSuccess({
//             token: undefined, // Don't update token
//             refreshToken: undefined, // Don't update refreshToken
//             user: data.user,
//             role: data.user.role,
//           }));
//         } catch (error) {
//           // Error handling can be done in the component
//           console.error('Profile update failed:', error);
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     // ====================================
//     // USER MANAGEMENT ENDPOINTS
//     // ====================================

//     // Get all users with pagination and filtering
//     getAllUsers: builder.query({
//       query: ({ page = 1, limit = 10, role, isActive, search } = {}) => {
//         const params = new URLSearchParams({
//           page: page.toString(),
//           limit: limit.toString(),
//         });

//         if (role) params.append('role', role);
//         if (isActive !== undefined) params.append('isActive', isActive.toString());
//         if (search) params.append('search', search);

//         return `/dashboarduser/users?${params.toString()}`;
//       },
//       providesTags: ['Users'],
//     }),

//     // Get user by ID
//     getUserById: builder.query({
//       query: (userId) => `/dashboarduser/users/${userId}`,
//       providesTags: (result, error, userId) => [{ type: 'Users', id: userId }],
//     }),

//     // Update user (Admin function)
//     updateUser: builder.mutation({
//       query: ({ userId, updateData }) => ({
//         url: `/dashboarduser/users/${userId}`,
//         method: 'PUT',
//         body: {
//           // Map frontend fields to backend expected fields
//           username: updateData.username,
//           email: updateData.email,
//           role: updateData.role,
//           serviceCode: updateData.serviceCode,
//           organization: updateData.organisation || updateData.organization, // Handle both field names
//           country: updateData.country,
//           stationName: updateData.stationName,
//           isActive: updateData.isActive,
//         },
//       }),
//       invalidatesTags: (result, error, { userId }) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     // Delete user
//     deleteUser: builder.mutation({
//       query: (userId) => ({
//         url: `/dashboarduser/users/${userId}`,
//         method: 'DELETE',
//       }),
//       invalidatesTags: (result, error, userId) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     // Toggle user active status
//     toggleUserStatus: builder.mutation({
//       query: (userId) => ({
//         url: `/dashboarduser/users/${userId}/toggle-status`,
//         method: 'PATCH',
//       }),
//       invalidatesTags: (result, error, userId) => [
//         'Users',
//         { type: 'Users', id: userId },
//       ],
//     }),

//     // Create user (Admin function) - Alternative to register for admins
//     createUser: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register', // Reuse register endpoint
//         method: 'POST',
//         body: {
//           username: userData.username,
//           email: userData.email,
//           password: userData.password,
//           role: userData.role,
//           serviceCode: userData.serviceCode,
//           organization: userData.organisation || userData.organization,
//           country: userData.country,
//           stationName: userData.stationName,
//         },
//       }),
//       invalidatesTags: ['Users'],
//     }),

//     // Bulk operations for user management
//     bulkUpdateUsers: builder.mutation({
//       query: ({ userIds, updateData }) => ({
//         url: '/dashboarduser/users/bulk-update',
//         method: 'PATCH',
//         body: {
//           userIds,
//           updateData: {
//             role: updateData.role,
//             isActive: updateData.isActive,
//             organization: updateData.organisation || updateData.organization,
//             country: updateData.country,
//           },
//         },
//       }),
//       invalidatesTags: ['Users'],
//     }),

//     // Get users by role (helper query)
//     getUsersByRole: builder.query({
//       query: (role) => `/dashboarduser/users?role=${role}&limit=1000`,
//       providesTags: (result, error, role) => [{ type: 'Users', id: `ROLE_${role}` }],
//     }),

//     // Get user statistics (for dashboard)
//     getUserStats: builder.query({
//       query: () => '/dashboarduser/users/stats',
//       providesTags: ['UserStats'],
//     }),
//   }),
// });

// export const {
//   // Existing auth hooks
//   useLoginMutation,
//   useRegisterMutation,
//   useLogoutMutation,
//   useRefreshTokenMutation,
//   useGetProfileQuery,
//   useUpdateProfileMutation,

//   // User management hooks
//   useGetAllUsersQuery,
//   useGetUserByIdQuery,
//   useUpdateUserMutation,
//   useDeleteUserMutation,
//   useToggleUserStatusMutation,
//   useCreateUserMutation,
//   useBulkUpdateUsersMutation,
//   useGetUsersByRoleQuery,
//   useGetUserStatsQuery,

//   // Lazy queries for conditional loading
//   useLazyGetAllUsersQuery,
//   useLazyGetUserByIdQuery,
//   useLazyGetUsersByRoleQuery,
// } = authApi;

// // src/store/api/authApi.js
// import { baseApi } from './baseApi';
// import { loginSuccess, loginFailure, loginStart, logout } from '../slices/authSlice';

// export const authApi = baseApi.injectEndpoints({
//   endpoints: (builder) => ({
//     login: builder.mutation({
//       query: (credentials) => ({
//         url: '/dashboarduser/login',
//         method: 'POST',
//         body: {
//           // Map frontend field to backend expected field
//           email: credentials.identifier, // Your login form uses 'identifier' but backend expects 'email'
//           password: credentials.password,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Add refreshToken support
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Login failed. Please check your credentials.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     register: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register',
//         method: 'POST',
//         body: {
//           // Map frontend fields to backend expected fields
//           username: userData.username,
//           email: userData.email,
//           password: userData.password,
//           role: userData.role,
//           serviceCode: userData.serviceCode,
//           organization: userData.organisation, // Frontend uses 'organisation', backend expects 'organization'
//           country: userData.country,
//           stationName: userData.stationName, // Optional field
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Add refreshToken support
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                            error.error?.data?.error ||
//                            'Registration failed. Please try again.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     logout: builder.mutation({
//       query: () => ({
//         url: '/dashboarduser/logout',
//         method: 'POST',
//       }),
//       async onQueryStarted(arg, { dispatch }) {
//         // Clear auth state immediately
//         dispatch(logout()); // Use the correct logout action
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     refreshToken: builder.mutation({
//       query: (refreshToken) => ({
//         url: '/dashboarduser/refresh',
//         method: 'POST',
//         body: { refreshToken }, // Send refreshToken in body as expected by backend
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             refreshToken: data.refreshToken, // Update with new refreshToken
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           dispatch(logout()); // Use the correct logout action
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     getProfile: builder.query({
//       query: () => '/dashboarduser/profile',
//       providesTags: ['Auth'],
//     }),

//     updateProfile: builder.mutation({
//       query: (updateData) => ({
//         url: '/dashboarduser/profile',
//         method: 'PUT',
//         body: {
//           // Map frontend fields to backend expected fields
//           username: updateData.username,
//           organization: updateData.organisation, // Frontend uses 'organisation'
//           country: updateData.country,
//           stationName: updateData.stationName,
//         },
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           // Update user data in state without affecting tokens
//           dispatch(loginSuccess({
//             token: undefined, // Don't update token
//             refreshToken: undefined, // Don't update refreshToken
//             user: data.user,
//             role: data.user.role,
//           }));
//         } catch (error) {
//           // Error handling can be done in the component
//           console.error('Profile update failed:', error);
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),
//   }),
// });

// export const {
//   useLoginMutation,
//   useRegisterMutation,
//   useLogoutMutation,
//   useRefreshTokenMutation,
//   useGetProfileQuery,
//   useUpdateProfileMutation, // Add the new mutation hook
// } = authApi;

// // src/store/api/authApi.js
// import { baseApi } from './baseApi';
// import { loginSuccess, loginFailure, loginStart } from '../slices/authSlice';

// export const authApi = baseApi.injectEndpoints({
//   endpoints: (builder) => ({
//     login: builder.mutation({
//       query: (credentials) => ({
//         url: '/dashboarduser/login',
//         method: 'POST',
//         body: credentials,
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                           error.error?.data?.error ||
//                           'Login failed. Please check your credentials.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     register: builder.mutation({
//       query: (userData) => ({
//         url: '/dashboarduser/register',
//         method: 'POST',
//         body: userData,
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         dispatch(loginStart());
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           const message = error.error?.data?.message ||
//                           error.error?.data?.error ||
//                           'Registration failed. Please try again.';
//           dispatch(loginFailure(message));
//         }
//       },
//       invalidatesTags: ['Auth'],
//     }),

//     logout: builder.mutation({
//       query: () => ({
//         url: '/dashboarduser/logout',
//         method: 'POST',
//       }),
//       async onQueryStarted(arg, { dispatch }) {
//         // Clear auth state immediately
//         dispatch({ type: 'auth/logout' });
//       },
//     }),

//     refreshToken: builder.mutation({
//       query: () => ({
//         url: '/dashboarduser/refresh',
//         method: 'POST',
//       }),
//       async onQueryStarted(arg, { dispatch, queryFulfilled }) {
//         try {
//           const { data } = await queryFulfilled;
//           dispatch(loginSuccess({
//             token: data.token,
//             user: data.user,
//             role: data.role || data.user?.role,
//           }));
//         } catch (error) {
//           dispatch({ type: 'auth/logout' });
//         }
//       },
//     }),

//     getProfile: builder.query({
//       query: () => '/dashboarduser/profile',
//       providesTags: ['Auth'],
//     }),
//   }),
// });

// export const {
//   useLoginMutation,
//   useRegisterMutation,
//   useLogoutMutation,
//   useRefreshTokenMutation,
//   useGetProfileQuery,
// } = authApi;
